import Button from './button'
import Icon, {icons} from './icon'
import Input from './input'
import PropTypes from 'prop-types'
import React, {Component} from 'react'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import sortBy from 'lodash/sortBy'
import {propsShallowDiff} from '../shared/utils'

let noop = () => {}

const FormikContext = React.createContext({})

// eslint-disable-next-line react/prop-types
let FormikWrapper = ({initialValues: values, render, children, ...rest}) => {
  const params = {
    errors: {},
    touched: {},
    values,
    handleBlur: noop,
    handleChange: noop,
    handleSubmit: noop,
    isSubmitting: false,
    ...rest
  }

  return (
    <FormikContext.Provider value={params}>
      {render
        ? render(params)
        : typeof children === 'function'
        ? children(params)
        : children}
    </FormikContext.Provider>
  )
}

let FormWrapper = props => <form {...props} />

let ErrorMessageWrapper = props => (
  <FormikContext.Consumer>
    {value => (value.errors[props.name] ? <div {...props} /> : null)}
  </FormikContext.Consumer>
)

ErrorMessageWrapper.propTypes = {
  name: PropTypes.string
}

const loadFormik = () =>
  import(/* webpackChunkName: "formik" */ 'formik').then(pkg => {
    FormikWrapper = pkg.Formik // eslint-disable-line no-class-assign
    FormWrapper = pkg.Form // eslint-disable-line no-class-assign
    ErrorMessageWrapper = pkg.ErrorMessage // eslint-disable-line no-class-assign
  })

export class Formik extends Component {
  componentDidMount() {
    loadFormik().then(() => this.forceUpdate())
  }

  render() {
    return <FormikWrapper {...this.props} />
  }
}

export class Form extends Component {
  componentDidMount() {
    loadFormik().then(() => this.forceUpdate())
  }

  render() {
    return <FormWrapper {...this.props} />
  }
}

export class ErrorMessage extends Component {
  componentDidMount() {
    loadFormik().then(() => this.forceUpdate())
  }

  render() {
    return <ErrorMessageWrapper {...this.props} />
  }
}

export class FormikEffect extends Component {
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(nextProps, this.props)) {
      this.props.onChange(nextProps, this.props)
    }
  }

  render() {
    return null
  }
}

FormikEffect.propTypes = {
  onChange: PropTypes.func.isRequired
}

const IGNORE_UPATE_PROPS = {
  errors: true,
  values: true,
  touched: true
}

export class StatusInput extends Component {
  shouldComponentUpdate(nextProps) {
    const {props} = this
    let updates = propsShallowDiff(props, nextProps)
    if (!updates.length) {
      return false
    }

    // Ignore updates if changes do not actually affect this component
    const {name} = props
    return (
      updates.filter(
        key =>
          !(IGNORE_UPATE_PROPS[key] && nextProps[key][name] == props[key][name])
      ).length > 0
    )
  }

  render() {
    const {
      children,
      inputComponent: Component,
      errors,
      handleBlur,
      handleChange,
      touched,
      values,
      ...rest
    } = this.props
    const {name} = this.props

    return (
      <>
        {children ? (
          children(name, values[name], {
            ...rest,
            handleChange: value => handleChange({target: {name, value}})
          })
        ) : (
          <Component
            onBlur={handleBlur}
            onChange={handleChange}
            valid={touched[name] ? !errors[name] : undefined}
            value={values[name] || ''}
            {...rest}
          />
        )}
        <ErrorMessage className="error-message" component="div" name={name} />
      </>
    )
  }
}

StatusInput.propTypes = {
  children: PropTypes.func,
  errors: PropTypes.object,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func,
  inputComponent: PropTypes.any,
  label: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  size: PropTypes.string,
  touched: PropTypes.object,
  type: PropTypes.string,
  values: PropTypes.object
}

StatusInput.defaultProps = {
  inputComponent: Input,
  size: 'block',
  type: 'text'
}

export const FormikInputs = ({
  components: {checkbox: CheckboxComponent, input: InputComponent} = {},
  disabled,
  keys = [],
  validationSchema,
  ...rest
}) => {
  const {fields = {}} = validationSchema.describe()
  keys = keys && keys.length ? keys : sortBy(Object.keys(fields))

  return keys.map(key => {
    const {
      type: yupType,
      meta: {type: inputType = 'text'} = {},
      label
    } = fields[key]

    const props = {name: key}

    if (yupType !== 'string' && yupType !== 'number' && yupType !== 'boolean') {
      throw new Error(
        'FormikInputs only supports the following Yup types: string, number, boolean!'
      )
    }

    if (yupType === 'boolean') {
      const {values, setFieldValue} = rest

      return (
        <CheckboxComponent
          key={key}
          checked={!!values[key]}
          disabled={disabled}
          readOnly
          onClick={() => setFieldValue(key, !values[key], true)}
          {...props}>
          {label}
        </CheckboxComponent>
      )
    }

    return (
      <InputComponent
        key={key}
        disabled={disabled}
        {...{
          label,
          type: inputType,
          ...props,
          ...pick(rest, Object.keys(InputComponent.propTypes))
        }}
      />
    )
  })
}

FormikInputs.propTypes = {
  components: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  keys: PropTypes.array,
  validationSchema: PropTypes.object.isRequired
}

export const FormikSchema = ({children, validationSchema, ...rest}) => {
  const {fields = {}} = validationSchema.describe()

  const initialValues = {}
  Object.keys(fields).forEach(field => {
    const schema = validationSchema.fields[field]
    initialValues[field] = schema._default !== undefined ? schema._default : ''
  })

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      {...rest}>
      {({handleSubmit, ...props}) => (
        <form onSubmit={handleSubmit}>
          {children({...props, validationSchema})}
        </form>
      )}
    </Formik>
  )
}

FormikSchema.propTypes = {
  children: PropTypes.func.isRequired,
  validationSchema: PropTypes.object.isRequired
}

export const LoadingButton = ({children, disabled, isSubmitting, ...rest}) => (
  <Button
    disabled={disabled || isSubmitting}
    {...pick(rest, Object.keys(Button.propTypes))}>
    {isSubmitting ? <Icon icon={icons.spinner} spin size="lg" /> : children}
  </Button>
)

LoadingButton.propTypes = {
  children: PropTypes.any,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool
}
